import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import Button from "~/components/buttons/Button";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import Input from "~/components/formElements/Input";
import SubmitButton from "~/components/formElements/SubmitButton";
import AddressFormSection from "~/components/formSections/AddressFormSection/AddressFormSection.tsx";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import useCountryCodeSelectOptions from "~/hooks/form/formOptionsData/useCountryCodeSelectOptions.ts";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import { createOrUpdateSellerProfile } from "~/modules/billing/sellerProfile/sellerProfileApiDispatchers.ts";
import type {
	CreateOrUpdateSellerProfileData,
	SellerProfile,
} from "~/modules/billing/sellerProfile/sellerProfileTypes.ts";
import type { AddressFormData } from "~/types/addressTypes.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type FormData = Omit<CreateOrUpdateSellerProfileData, "address"> & AddressFormData;

type Props = {
	onCancel: () => void;
	onSuccess: () => void;
	sellerProfileData?: SellerProfile;
};

const CreateOrUpdateSellerProfileForm = ({
	onCancel,
	onSuccess,
	sellerProfileData,
}: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");

	const countryCodeSelectOptions = useCountryCodeSelectOptions();

	const schema = useMemo(() => {
		return yup.object({
			addressLine1: yup.string().default(null),
			addressLine2: yup.string().default(null),
			addressLine3: yup.string().default(null),
			city: yup.string().default(null),
			countryCode: yup.string().default(null),
			postCode: yup.string().default(null),
			name: yup.string().required(),
			vatIdentifier: yup.string().required(),
			contactName: yup.string().required(),
			contactPhone: yup.string().required(),
			contactEmail: yup.string().email().required(),
			electronicAddress: yup.string().email().required(),
			bankIban: yup.string().required(),
			bankBic: yup.string().required(),
			bankName: yup.string().required(),

		});
	}, []);
	const defaultValues = useMemo(() => {
		return {
			addressLine1: "Türme 2",
			addressLine2: "",
			addressLine3: "",
			city: "Hamburg",
			countryCode: "DE",
			postCode: "20359",
			name: "NORD.NORD BMG GmbH",
			vatIdentifier: "DE123456789",
			contactName: "Susanne Hofrichter",
			contactPhone: "040 123456789",
			contactEmail: "susanne@nordnord.com",
			electronicAddress: "invoice@nordnord.com",
			bankIban: "DE12345678901234567890",
			bankBic: "GENODEF1S01",
			bankName: "GLS Bank",
		};
		return {
			addressLine1: sellerProfileData?.address?.addressLine1 ?? "",
			addressLine2: sellerProfileData?.address?.addressLine2 ?? "",
			addressLine3: sellerProfileData?.address?.addressLine3 ?? "",
			city: sellerProfileData?.address?.city ?? "",
			countryCode: sellerProfileData?.address?.countryCode ?? "",
			postCode: sellerProfileData?.address?.postCode ?? "",
			name: sellerProfileData?.name ?? "",
			vatIdentifier: sellerProfileData?.vatIdentifier ?? "",
			contactName: sellerProfileData?.contactName ?? "",
			contactPhone: sellerProfileData?.contactPhone ?? "",
			contactEmail: sellerProfileData?.contactEmail ?? "",
			electronicAddress: sellerProfileData?.electronicAddress ?? "",
			bankIban: sellerProfileData?.bankIban ?? "",
			bankBic: sellerProfileData?.bankBic ?? "",
			bankName: sellerProfileData?.bankName ?? "",
		};
	}, [sellerProfileData?.address?.addressLine1, sellerProfileData?.address?.addressLine2, sellerProfileData?.address?.addressLine3, sellerProfileData?.address?.city, sellerProfileData?.address?.countryCode, sellerProfileData?.address?.postCode, sellerProfileData?.bankBic, sellerProfileData?.bankIban, sellerProfileData?.bankName, sellerProfileData?.contactEmail, sellerProfileData?.contactName, sellerProfileData?.contactPhone, sellerProfileData?.electronicAddress, sellerProfileData?.name, sellerProfileData?.vatIdentifier]);

	const {
		handleSubmit,
		control,
		formState: { isDirty, isSubmitted, isValid },
	} = useForm<FormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<FormData>(schema),
	});

	const formIsSubmittable = useFormIsSubmittable({
		isSubmitted,
		isDirty,
		isValid,
		isLoading: isBusy,
	});

	const onSubmit = useCallback(async (data: FormData) => {
		try {
			setIsBusy(true);
			const { addressLine1, addressLine2, addressLine3, city, postCode, countryCode, ...rest } = data;
			const updateData: CreateOrUpdateSellerProfileData = {
				...rest,
				address: {
					addressLine1,
					addressLine2,
					addressLine3,
					city,
					postCode,
					countryCode,
				},
			};
			await createOrUpdateSellerProfile(updateData);
			onSuccess();
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
			setIsBusy(false);
		}
	}, [onSuccess]);

	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>E-Rechnung Firmenprofil</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Request fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
				<FormSectionGroup>
					<FormSection title="Firmendaten">
						<div className="space-y-2">
							<Input control={control}
								   name="name"
								   label="Name Legaleinheit"
								   placeholder="NORD.NORD BMG GmbH"
								   required />
							<Input control={control}
								   name="vatIdentifier"
								   label="Umsatzsteuer-ID"
								   placeholder="DE123456789"
								   required />
							<Input control={control}
								   name="electronicAddress"
								   label="Elektronische Adresse (E-Mail)"
								   placeholder="invoice@nordnord.com" />
						</div>
						<div className="mt-0.5 text-xs text-gray-500">Diese E-Mail Adresse wird für automatisierte Kommunikation im E-Rechnungsvorgang verwendet.</div>
					</FormSection>
					<AddressFormSection control={control}
										countryCodeSelectOptions={countryCodeSelectOptions} />
					<FormSection title="Bankverbindung">
						<div className="space-y-2">
							<Input control={control}
								   name="bankIban"
								   label="IBAN"
								   placeholder="DE12345678901234567890"
								   required />
							<Input control={control}
								   name="bankBic"
								   label="BIC"
								   placeholder="DEUTDEFFXXX"
								   required />
							<Input control={control}
								   name="bankName"
								   label="Name des Kreditinstituts"
								   placeholder="Deutsche Bank"
								   required />
						</div>
					</FormSection>
					<FormSection title="Kontakt für Rückfragen">
						<div className="space-y-2">
							<Input control={control}
								   name="contactName"
								   label="Name"
								   placeholder="Vorname Nachname"
								   required />
							<Input control={control}
								   name="contactPhone"
								   label="Telefon"
								   placeholder="+49 123 4567890"
								   required />
							<Input control={control}
								   name="contactEmail"
								   label="E-Mail"
								   placeholder="Kontakt@nordnord.com" />
						</div>
					</FormSection>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable}>
					{t("form:buttonTexts.save")}
				</SubmitButton>
				<Button theme="white"
						onClick={onCancel}>
					{t("form:buttonTexts.cancel")}
				</Button>
			</SidebarFooter>
		</form>);
};

export default CreateOrUpdateSellerProfileForm;
